<script setup>
import axios from '@axios'
import { reviewLine } from '@/utils/requestApproval'
import moment from 'moment-timezone'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useReservationsStore, useUsersStore } from '@/stores'
import { numberWithCommas } from '@/plugins/misc'
import Tooltip from '@/layouts/components/Tooltip.vue'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import { budgetSummarySuffix } from '@/utils/inquiryCopy'

const props = defineProps({
  selectedRestaurants: { type: Array, default: () => [] },
  restaurantsForSelectedCity: { type: Array, default: () => [] },
  allTimeOptions: { type: Array, default: () => [] },
  selectedRestaurantsNotAcceptingReferrals: { type: Array, default: null },
  dateContextSubtitle: { type: String, default: '' },
  dateSelectedPastPreferredMembershipTerm: { type: Boolean, default: false },
  isNotHost: { type: Boolean, default: false },
  isProfileIncomplete: { type: Boolean, default: false },
  profileValidationMessage: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  // RM-400 (Kapil, 10/13): what each restaurant will receive — the inquiry email, as RM-360's request preview
  previewLabel: { type: String, default: '' },
  openingPreview: { type: Boolean, default: false },
  previewError: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'upgrade', 'preview'])

const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const { myUser, loginDialogMode } = storeToRefs(usersStore)
const { editingReservation } = storeToRefs(reservationsStore)

// RM-359: will RM review this inquiry before it's sent, and for which
// restaurants? (the server decides; yes/no and the names only). A
// group/private inquiry goes to the others right away.
const approvalNeeded = ref(false)
const approvalRestaurants = ref([])
watch(() => editingReservation.value?._id, async id => {
  approvalNeeded.value = false
  approvalRestaurants.value = []
  if (!id) return
  try {
    const { data } = await axios.get(`/reservation/${id}/approval-check`)
    approvalNeeded.value = !!data?.approvalNeeded
    approvalRestaurants.value = Array.isArray(data?.restaurants) ? data.restaurants : []
  } catch (error) {
    approvalNeeded.value = false
  }
}, { immediate: true })

// RM-150: gate membership-dependent UI on the OWNER, not the actor.
// RM-154 follow-up: see EditReservationDialog.vue for the partial-owner-object
// flaw and the truthy-wins rationale. Normalize email comparison, allow
// ObjectId/string _id matching, and truthy-union membership flags for
// self-bookings so a stale owner stub doesn't override myUser's truthy state.
const reservationOwner = computed(() => {
  const owner = editingReservation.value?.owner
  const me = myUser.value
  if (!owner) return me || {}
  if (!owner.email && !owner._id) return me || {}

  const ownerEmail = String(owner.email || '').toLowerCase().trim()
  const meEmail = String(me?.email || '').toLowerCase().trim()
  const isSelfBooking = me && (
    (ownerEmail && ownerEmail === meEmail)
    || (owner._id && me._id && String(owner._id) === String(me._id))
  )

  if (isSelfBooking) {
    return {
      ...me,
      ...owner,
      isPlatinum: !!(owner.isPlatinum || me.isPlatinum),
      isPreferred: !!(owner.isPreferred || me.isPreferred),
      activeSubscription: owner.activeSubscription || me.activeSubscription,
    }
  }

  return owner
})
</script>

<template>
  <div>
    <div class="review-step-header">
      <span class="review-step-title">Review & Submit</span>
      <span
        v-if="dateContextSubtitle"
        class="review-step-subtitle"
      >{{ dateContextSubtitle }}</span>
    </div>
    <div class="mx-4 mb-8 tab-content">
      <div
        v-if="editingReservation"
        class="review-summary-card mb-4"
      >
        <div class="review-restaurant-name">
          <span
            v-for="(restaurant, index) in selectedRestaurants"
            :key="restaurant._id || index"
          >{{ restaurant.name }}<span v-if="index < selectedRestaurants.length - 1">, </span></span>
          <span
            v-if="selectedRestaurants.length === 1 && selectedRestaurants[0]?.market"
            class="review-restaurant-market"
          >{{ selectedRestaurants[0].market }}</span>
        </div>

        <div class="review-details-grid">
          <div class="review-detail-item">
            <span class="review-detail-label">Date</span>
            <span class="review-detail-value">{{ editingReservation.date ? moment(editingReservation.date).format('dddd, M/D') : '' }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">Party Size</span>
            <span class="review-detail-value">{{ editingReservation.partySize }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">Preferred Time</span>
            <span class="review-detail-value">{{ editingReservation.preferredTime }}</span>
          </div>
          <div
            v-if="editingReservation.earliestTime && editingReservation.latestTime"
            class="review-detail-item"
          >
            <span class="review-detail-label">Time Range</span>
            <span class="review-detail-value">{{ editingReservation.earliestTime }} – {{ editingReservation.latestTime }}</span>
          </div>
          <div
            v-if="editingReservation.occasion"
            class="review-detail-item"
          >
            <span class="review-detail-label">Occasion</span>
            <span class="review-detail-value">{{ editingReservation.occasion }}</span>
          </div>
          <div
            v-if="editingReservation.roomPreference"
            class="review-detail-item"
          >
            <span class="review-detail-label">Room</span>
            <span class="review-detail-value">{{ editingReservation.roomPreference }}</span>
          </div>
          <div
            v-if="editingReservation.seatingPreference"
            class="review-detail-item"
          >
            <span class="review-detail-label">Seating</span>
            <span class="review-detail-value">{{ editingReservation.seatingPreference }}</span>
          </div>
          <div class="review-detail-item">
            <span class="review-detail-label">F&B Budget</span>
            <span class="review-detail-value">${{ numberWithCommas(editingReservation.partySize * editingReservation.minSpendPerPerson) }} (${{ numberWithCommas(editingReservation.minSpendPerPerson) }}pp)<template v-if="budgetSummarySuffix(editingReservation.budgetFlexibility)"> - {{ budgetSummarySuffix(editingReservation.budgetFlexibility) }}</template></span>
          </div>
          <div
            v-if="editingReservation.specialRequests"
            class="review-detail-item review-detail-item--full"
          >
            <span class="review-detail-label">Notes / Requests</span>
            <span class="review-detail-value">{{ editingReservation.specialRequests }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="editingReservation"
        class="review-profile-card-wrapper"
      >
        <div class="review-profile-nudge">
          <div class="review-profile-nudge-text">
            <VIcon
              size="16"
              icon="mdi-information-outline"
              class="mr-1"
            />
            {{ $t('reservation.labels.restaurantsReviewProfile') }}
          </div>
          <VBtn
            v-if="myUser?.userLevel != 'dummy'"
            variant="outlined"
            size="x-small"
            color="primary-dark"
            class="review-profile-nudge-btn"
            @click="reservationsStore.saveEditReservation(true); usersStore.editMyUser()"
          >
            Edit Profile
          </VBtn>
        </div>
        <MemberProfileCard :member="editingReservation.owner || myUser" />
      </div>

      <template v-if="myUser.userLevel == 'admin' && editingReservation.status == 'Accepted'">
        <VCol cols="12">
          <VCombobox
            v-model="editingReservation.confirmedRestaurant"
            :label="$t('reservation.labels.confirmedRestaurant')"
            :items="restaurantsForSelectedCity"
            :menu-props="{height:($vuetify.display.mdAndDown ? 250 : 650) }"
            :hide-no-data="false"
            item-title="text"
            item-value="value"
            :return-object="false"
            chips
            closable-chips
            required
            hide-selected
          >
            <template #prepend-inner>
              <VIcon>mdi-magnify</VIcon>
            </template>
            <template #no-data>
              <VListItem>
                <VListItemTitle>
                  {{ $t('reservation.labels.noMatchesWriteIn') }}
                </VListItemTitle>
              </VListItem>
            </template>
          </VCombobox>
        </VCol>
        <VCol cols="12">
          <Tooltip text="The time the restaurant used to make the reservation." />
          <VSelect
            v-model="editingReservation.confirmedTime"
            :label="$t('reservation.labels.confirmedTime')"
            :items="allTimeOptions"
            :no-data-text="$t('reservation.labels.noTimesAvailable')"
            :disabled="!editingReservation.date"
          />
        </VCol>
      </template>

      <div
        v-if="editingReservation.status == 'Draft'"
        class="mt-4"
      >
        <VAlert
          v-if="reservationOwner.activeSubscription && dateSelectedPastPreferredMembershipTerm"
          class="mb-2"
          type="warning"
        >
          <div>{{ $t('reservation.validation.datePastMembership') }}</div>
          <div class="mt-2">
            {{ $t('reservation.validation.questionsEmail') }} <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
        <VAlert
          v-else-if="!reservationOwner.activeSubscription"
          class="mb-2"
          type="warning"
        >
          <div>{{ $t('reservation.validation.upgradeRequired') }}</div>
          <div class="mt-2">
            {{ $t('reservation.validation.questionsEmail') }} <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
        <VAlert
          v-else-if="!isNotHost && myUser.activeSubscription && isProfileIncomplete"
          class="mb-2"
          type="warning"
        >
          <div>{{ profileValidationMessage }}{{ $t('reservation.validation.profileIncomplete') }}</div>
          <div class="mt-2">
            {{ $t('reservation.validation.questionsEmail') }} <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
          </div>
        </VAlert>
      </div>

      <VCol
        cols="12"
        class="d-flex flex-wrap justify-center gap-4 px-0"
      >
        <VSpacer />
        <template v-if="myUser?.primaryGroupPermission == 'pending'">
          <VBtn
            v-if="isProfileIncomplete"
            @click="reservationsStore.saveEditReservation(true); usersStore.editMyUser()"
          >
            {{ $t('reservation.actions.completeProfile') }}
          </VBtn>
          <VBtn
            v-else
            @click="reservationsStore.saveEditReservation();"
          >
            {{ $t('reservation.actions.saveAndClose') }}
          </VBtn>
        </template>
        <template v-else-if="editingReservation.status == 'Draft'">
          <div
            v-if="myUser?.userLevel == 'dummy'"
            class="text-center w-100"
          >
            <VBtn
              variant="flat"
              class="px-6"
              size="small"
              color="primary"
              @click="loginDialogMode = 'activate';"
            >
              <span>{{ $t('menu.requestToJoin') }}</span>
            </VBtn>
            <VBtn
              variant="flat"
              class="ml-2"
              size="small"
              color="#efefef"
              style="color: #222222;"
              @click="loginDialogMode = 'login';"
            >
              <span>{{ $t('menu.logIn') }}</span>
            </VBtn>
          </div>
          <VBtn
            v-else-if="reservationOwner.activeSubscription && dateSelectedPastPreferredMembershipTerm"
            @click="emit('upgrade')"
          >
            {{ $t('reservation.actions.extendMembership') }}
          </VBtn>
          <VBtn
            v-else-if="!isNotHost && myUser.activeSubscription && isProfileIncomplete"
            @click="reservationsStore.saveEditReservation(true); usersStore.editMyUser()"
          >
            {{ $t('reservation.actions.completeProfile') }}
          </VBtn>
          <div
            v-else-if="selectedRestaurantsNotAcceptingReferrals"
            class="text-center w-100"
          >
            {{ $t('reservation.preferred.notAcceptingReferralsGoBack') }}
          </div>
          <template v-else-if="reservationOwner.activeSubscription">
            <div class="w-100">
              <div
                v-if="editingReservation?._id && previewLabel && myUser?.userLevel !== 'dummy'"
                class="mb-3"
              >
                <RequestPreviewCard
                  :label="previewLabel"
                  :loading="openingPreview"
                  @open="emit('preview')"
                />
                <div
                  v-if="previewError"
                  class="text-caption text-error mt-1 text-center"
                >
                  Couldn't open the preview. Try again
                </div>
              </div>
              <VBtn
                :disabled="saving || !editingReservation.preferredRestaurant"
                color="primary-dark"
                size="large"
                block
                class="submit-request-btn"
                @click="emit('submit')"
              >
                {{ $t('reservation.actions.submit') }}
              </VBtn>
              <div class="submit-footer-text">
                <!-- RM-359: never "sent instantly" about an inquiry RM reviews first -->
                <template v-if="approvalNeeded">{{ reviewLine(approvalRestaurants, { noun: 'inquiry' }) }}</template>
                <template v-else>{{ $t('reservation.private.inquirySent') }}</template>
                · {{ $t('reservation.private.memberCcd', { email: myUser?.email }) }}
              </div>
              <div
                v-if="myUser?.email != editingReservation.owner?.email"
                class="submit-footer-text"
              >
                {{ $t('reservation.private.onlySubmittingCcd') }}
              </div>
              <!-- RM-359 Part B: RM's view before submitting; never submits -->
              <AskRmFeedback
                v-if="editingReservation?._id && editingReservation?.status === 'Draft'"
                :reservation-id="editingReservation._id"
                class="mt-2"
              />
            </div>
          </template>
          <VBtn
            v-else
            @click="emit('upgrade')"
          >
            {{ $t('reservation.actions.upgradeMembership') }}
          </VBtn>
        </template>
        <template v-else-if="editingReservation.submittedAt">
          <VBtn @click="reservationsStore.saveEditReservation();">
            {{ $t('reservation.actions.save') }}
          </VBtn>
        </template>
        <VSpacer />
      </VCol>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tab-content {
  position: relative;
}

.review-step-header {
  text-align: center;
  padding: 12px 0 4px;
}

.review-step-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.review-step-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  display: block;
  margin-top: 4px;
}

.review-summary-card {
  background: #f8f8f9;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.review-restaurant-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
  line-height: 1.3;
}

.review-restaurant-market {
  font-size: 14px;
  font-weight: 400;
  color: #999;
  display: block;
  margin-top: 2px;
}

.review-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.review-detail-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.review-detail-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.review-detail-value {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

// Special Requests is a field like the others (Kapil, 10/21): the same label and value, across both columns.
.review-detail-item--full {
  grid-column: 1 / -1;
}

.review-profile-card-wrapper {
  margin-top: 16px;
}

.review-profile-nudge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: var(--ds-brand-primary-light);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
}

.review-profile-nudge-text {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-brand-primary-dark);
}

.review-profile-nudge-btn {
  flex-shrink: 0;
}

.submit-request-btn {
  font-weight: 700;
  letter-spacing: 0.02em;
  max-width: 320px;
  margin: 0 auto;
  display: block;
}

.submit-footer-text {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
</style>
